/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, Component, Inject, OnInit, signal} from '@angular/core';
import * as _ from 'lodash';
import {finalize, firstValueFrom} from 'rxjs';

import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {buildElectionFormOptions, buildElectionSelectGroups, canUseTestElectionsInForms, sameElectionId} from '../../../components/util/election-form-options';
import {normalizePreviewLanguageSelection, PREVIEW_LANGUAGE_OPTIONS, resolvePreviewLanguageCode} from '../../creative/ad/preview-language';
import {
  expandPoliticalPreviewFullScreenDocument,
  findPoliticalOverlayElement,
  formatPoliticalPreviewDate,
  getDefaultPoliticalPreviewElectionId,
  getPoliticalPreviewImageUrl,
  getPoliticalPreviewLayoutParameters,
  injectPoliticalPreviewHtml,
  parsePoliticalPreviewDate,
  getPoliticalPreviewRenderTemplate,
  shouldLoadPoliticalPreviewElections
} from './political-preview.util';
import template from './political-preview-page.component.html';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {WINDOW} from '../../angular-platform-providers';
import {deferSignalStateWrite} from '../../common/util/template-state';
import {ElectionApiService} from '../../common/controller/election-api.service';
import {CreativePreviewApiService} from '../../creative/creative-preview-api.service';

@Component({
  standalone: false,
  selector: 'adn-political-preview-page',
  template
})
export class PoliticalPreviewPageComponent implements OnInit {
  form = {
    electionInputMode: 'existing',
    selectedElectionId: '',
    electionName: '',
    electionDate: '' as Date | '',
    advertiserName: 'United Nations',
    controllingEntityName: 'United Nations',
    controllingEntitySameAsSponsor: true,
    hasUserTargeting: false,
    destinationUrl: 'https://example.com',
    imageSource: 'hosted',
    hostedImageUrl: 'https://assets.adnuntius.com/cgaeda/67b69k75g97mrym79mwqhspyylp3nq8ph6rzx5fj83hb563ztc.png',
    layout: 'standard',
    creativeWidth: 400,
    creativeHeight: 400,
    uploadedImageContent: '',
    uploadedImageMimeType: '',
    uploadedImageName: ''
  };
  private readonly electionsState = signal<any[]>([]);
  private readonly electionSelectGroupsState = signal({dated: [] as any[], undated: [] as any[], useGroups: false, groups: [] as any[]});
  private readonly electionsLoadingState = signal(false);
  private readonly electionsLoadErrorState = signal('');
  private readonly errorMessageState = signal<any>(null);
  private readonly loadingState = signal(false);
  private readonly hasPreviewState = signal(false);
  private readonly previewedLayoutState = signal('');
  private readonly previewHtmlState = signal('');
  private readonly imageErrorState = signal('');
  private readonly overlayMetricsState = signal<{width: number; height: number; coveragePercentage: number} | null>(null);
  private readonly overlayMetricsErrorState = signal('');

  get elections(): any[] { return this.electionsState(); }
  set elections(value: any[]) { this.electionsState.set(value); }
  get electionSelectGroups(): {dated: any[]; undated: any[]; useGroups: boolean; groups: any[]} { return this.electionSelectGroupsState(); }
  set electionSelectGroups(value: {dated: any[]; undated: any[]; useGroups: boolean; groups: any[]}) { this.electionSelectGroupsState.set(value); }
  get electionsLoading(): boolean { return this.electionsLoadingState(); }
  set electionsLoading(value: boolean) { this.electionsLoadingState.set(value); }
  get electionsLoadError(): string { return this.electionsLoadErrorState(); }
  set electionsLoadError(value: string) { this.electionsLoadErrorState.set(value); }
  get errorMessage(): any { return this.errorMessageState(); }
  set errorMessage(value: any) { this.errorMessageState.set(value); }
  get loading(): boolean { return this.loadingState(); }
  set loading(value: boolean) { this.loadingState.set(value); }
  get hasPreview(): boolean { return this.hasPreviewState(); }
  set hasPreview(value: boolean) { this.hasPreviewState.set(value); }
  get previewedLayout(): string { return this.previewedLayoutState(); }
  set previewedLayout(value: string) { this.previewedLayoutState.set(value); }
  get previewHtml(): string { return this.previewHtmlState(); }
  set previewHtml(value: string) { this.previewHtmlState.set(value); }
  get imageError(): string { return this.imageErrorState(); }
  set imageError(value: string) { this.imageErrorState.set(value); }
  get overlayMetrics(): {width: number; height: number; coveragePercentage: number} | null { return this.overlayMetricsState(); }
  set overlayMetrics(value: {width: number; height: number; coveragePercentage: number} | null) { this.overlayMetricsState.set(value); }
  get overlayMetricsError(): string { return this.overlayMetricsErrorState(); }
  set overlayMetricsError(value: string) { this.overlayMetricsErrorState.set(value); }
  hasAutoRendered = false;
  previewLanguageOptions = PREVIEW_LANGUAGE_OPTIONS;
  previewLanguage = normalizePreviewLanguageSelection('browser');
  error = '';
  publicError: string | null = null;
  previewRequest: any = null;

  private readonly username: string;
  private readonly includeTestElections: boolean;
  private overlayMeasureGeneration = 0;
  private overlayMeasureTimers: Array<ReturnType<typeof setTimeout>> = [];
  private iframeConstrainTimers: Array<ReturnType<typeof setTimeout>> = [];
  private previewIframeGeneration = 0;

  constructor(
    @Inject(DOCUMENT) private readonly document: any,
    @Inject(WINDOW) private readonly window: any,
    private readonly creativePreviewApi: CreativePreviewApiService,
    private readonly electionApi: ElectionApiService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    this.username = this.localUserProfile.get('username');
    this.includeTestElections = canUseTestElectionsInForms({
      username: this.username,
      canSeeTestElections: this.localUserProfile.canSeeTestElections()
    });
  }

  ngOnInit(): void {
    this.loadElections(false);
  }

  get isPreviewedFullScreen(): boolean {
    return this.previewedLayout === 'fullScreen';
  }

  getSelectedLanguageCode(): string {
    return resolvePreviewLanguageCode(this.previewLanguage, this.window.navigator);
  }

  getSelectedElection(): any {
    return _.find(this.elections, (election) => {
      return sameElectionId(election.id, this.form.selectedElectionId);
    }) || null;
  }

  applySelectedElection(): void {
    const selectedElection = this.getSelectedElection();
    if (!selectedElection) {
      return;
    }
    this.form.electionName = selectedElection.name || '';
    this.form.electionDate = parsePoliticalPreviewDate(selectedElection.electionDate);
  }

  setElectionInputMode(mode: string): void {
    this.form.electionInputMode = mode;
    if (mode === 'existing') {
      this.applySelectedElection();
    }
  }

  sponsorNameChanged(advertiserName: string): void {
    if (this.form.controllingEntitySameAsSponsor) {
      this.form.controllingEntityName = advertiserName;
    }
  }

  controllingEntitySameAsSponsorChanged(sameAsSponsor: boolean): void {
    if (sameAsSponsor) {
      this.form.controllingEntityName = this.form.advertiserName;
    }
  }

  applyElections(elections: any[]): void {
    this.elections = elections;
    this.electionSelectGroups = buildElectionSelectGroups(this.elections);
    if (!this.form.selectedElectionId && this.elections.length > 0) {
      this.form.selectedElectionId = getDefaultPoliticalPreviewElectionId(this.elections);
      this.applySelectedElection();
    }
  }

  loadElections(forceApiLoad: boolean): void {
    this.electionsLoadError = '';
    const cachedElections = buildElectionFormOptions(this.localNetworkInfo.getSuppInfo('elections') || [], {
      username: this.username,
      canSeeTestElections: this.localUserProfile.canSeeTestElections(),
      onlyFuture: true
    });
    const shouldRefresh = forceApiLoad
      || this.localNetworkInfo.shouldRefreshElectionsCache()
      || shouldLoadPoliticalPreviewElections(cachedElections);
    if (cachedElections.length > 0) {
      this.applyElections(cachedElections);
      if (!shouldRefresh) {
        this.tryInitialRender();
        return;
      }
    }

    this.electionsLoading = true;
    this.electionApi.list({includeTests: this.includeTestElections}).pipe(
      finalize(() => {
        this.electionsLoading = false;
        this.tryInitialRender();
      })
    ).subscribe({
      next: (data: any) => {
        this.localNetworkInfo.setSuppInfo('elections', _.get(data, 'results', []));
        this.applyElections(buildElectionFormOptions(_.get(data, 'results', []), {
          username: this.username,
          canSeeTestElections: this.localUserProfile.canSeeTestElections(),
          onlyFuture: true
        }));
      },
      error: () => {
        this.electionsLoadError = 'We could not load elections right now.';
      }
    });
  }

  writePreviewIframe(html: string, imageUrl?: string): void {
    const apply = () => {
      const host = this.document.getElementById('politicalPreviewFrameHost');
      if (!host) {
        return false;
      }

      this.cancelIframeConstrain();
      this.previewIframeGeneration += 1;
      const generation = this.previewIframeGeneration;
      host.innerHTML = '';

      if (!html) {
        return true;
      }

      const iframe = this.document.createElement('iframe');
      iframe.id = 'politicalPreviewFrame';
      iframe.title = 'Political preview';
      iframe.frameBorder = '0';
      iframe.setAttribute('scrolling', 'no');
      iframe.className = 'politicalPreviewIframe' + (this.isPreviewedFullScreen ? ' politicalPreviewIframeFullScreen' : '');
      this.constrainPreviewIframe(iframe);
      iframe.onload = () => {
        if (generation !== this.previewIframeGeneration) {
          return;
        }
        this.constrainPreviewIframe(iframe);
        this.applyPreviewImage(iframe, imageUrl);
        if (!this.overlayMetrics) {
          this.scheduleOverlayMeasurement();
        }
      };
      host.appendChild(iframe);
      iframe.setAttribute('srcdoc', html);
      this.applyPreviewImage(iframe, imageUrl);
      this.scheduleIframeConstrain(iframe, imageUrl, generation);
      this.scheduleOverlayMeasurement();
      return true;
    };

    if (!apply()) {
      setTimeout(() => {
        apply();
      }, 0);
    }
  }

  applyPreviewImage(iframe: HTMLElement, imageUrl?: string): void {
    if (!iframe || !iframe.isConnected) {
      return;
    }
    const iframeDocument = _.get(iframe, 'contentDocument') || _.get(iframe, ['contentWindow', 'document']);
    if (!iframeDocument || !iframeDocument.querySelectorAll) {
      return;
    }
    if (this.isPreviewedFullScreen) {
      expandPoliticalPreviewFullScreenDocument(iframeDocument);
    }
    if (!imageUrl) {
      return;
    }
    const cssUrl = 'url(' + JSON.stringify(imageUrl) + ')';
    const backgroundNodes = iframeDocument.querySelectorAll('.backgroundImage');
    _.forEach(backgroundNodes, (el: HTMLElement) => {
      el.style.backgroundImage = cssUrl;
      _.forEach(el.querySelectorAll('img'), (img: HTMLImageElement) => {
        img.src = imageUrl;
      });
    });
    if (backgroundNodes.length) {
      return;
    }
    const creativeImage = iframeDocument.querySelector('a img, img');
    if (creativeImage && !(creativeImage.closest && creativeImage.closest('.adn-ttpa-container'))) {
      (creativeImage as HTMLImageElement).src = imageUrl;
    }
  }

  constrainPreviewIframe(iframe: HTMLElement): void {
    if (!iframe || !iframe.isConnected) {
      return;
    }
    const width = Number(_.get(this, ['previewRequest', 'creativeWidth'])) || Number(this.form.creativeWidth) || 0;
    const height = Number(_.get(this, ['previewRequest', 'creativeHeight'])) || Number(this.form.creativeHeight) || 0;
    iframe.removeAttribute('style');
    iframe.style.display = 'block';
    iframe.style.border = '0';
    iframe.style.margin = '0';
    iframe.style.padding = '0';
    iframe.style.position = 'static';
    iframe.style.top = 'auto';
    iframe.style.left = 'auto';
    iframe.style.right = 'auto';
    iframe.style.bottom = 'auto';
    if (this.isPreviewedFullScreen) {
      iframe.removeAttribute('width');
      iframe.removeAttribute('height');
      iframe.style.width = '100%';
      iframe.style.maxWidth = '100%';
      iframe.style.minWidth = '0';
      iframe.style.height = '70vh';
      iframe.style.minHeight = '480px';
      return;
    }
    iframe.style.maxWidth = 'none';
    iframe.style.minWidth = '0';
    iframe.style.minHeight = '0';
    iframe.style.width = width ? width + 'px' : '';
    iframe.style.height = height ? height + 'px' : '';
    if (width) {
      iframe.setAttribute('width', String(width));
    } else {
      iframe.removeAttribute('width');
    }
    if (height) {
      iframe.setAttribute('height', String(height));
    } else {
      iframe.removeAttribute('height');
    }
  }

  cancelIframeConstrain(): void {
    _.forEach(this.iframeConstrainTimers, (timer) => {
      clearTimeout(timer);
    });
    this.iframeConstrainTimers = [];
  }

  scheduleIframeConstrain(iframe: HTMLElement, imageUrl: string | undefined, generation: number): void {
    this.cancelIframeConstrain();
    _.forEach([50, 200, 500], (delay) => {
      const timer = setTimeout(() => {
        if (generation !== this.previewIframeGeneration) {
          return;
        }
        this.constrainPreviewIframe(iframe);
        this.applyPreviewImage(iframe, imageUrl);
      }, delay);
      this.iframeConstrainTimers.push(timer);
    });
  }

  resetOverlayMetrics(): void {
    this.cancelOverlayMeasurement();
    this.overlayMetrics = null;
    this.overlayMetricsError = '';
  }

  cancelOverlayMeasurement(): void {
    this.overlayMeasureGeneration += 1;
    _.forEach(this.overlayMeasureTimers, (timer) => {
      clearTimeout(timer);
    });
    this.overlayMeasureTimers = [];
  }

  measureOverlayMetrics(): boolean {
    const iframe = this.document.getElementById('politicalPreviewFrame');
    const iframeDocument = _.get(iframe, 'contentDocument') || _.get(iframe, ['contentWindow', 'document']);
    const overlayElement = findPoliticalOverlayElement(iframeDocument);
    const iframeWidth = Number(_.get(iframe, 'clientWidth')) || 0;
    const iframeHeight = Number(_.get(iframe, 'clientHeight')) || 0;
    const creativeWidth = iframeWidth || Number(_.get(this, ['previewRequest', 'creativeWidth'])) || 0;
    const creativeHeight = iframeHeight || Number(_.get(this, ['previewRequest', 'creativeHeight'])) || 0;

    if (!overlayElement || !creativeWidth || !creativeHeight) {
      return false;
    }

    const rect = overlayElement.getBoundingClientRect();
    const overlayWidth = Math.round(rect.width);
    const overlayHeight = Math.round(rect.height);

    if (!overlayWidth || !overlayHeight) {
      return false;
    }

    const coveragePercentage = Math.round((overlayWidth * overlayHeight) / (creativeWidth * creativeHeight) * 100);

    this.overlayMetrics = {
      width: overlayWidth,
      height: overlayHeight,
      coveragePercentage: coveragePercentage
    };
    this.overlayMetricsError = '';

    return true;
  }

  scheduleOverlayMeasurement(): void {
    this.cancelOverlayMeasurement();
    const generation = this.overlayMeasureGeneration;
    // Prefer rAF (layout-ready) over the old 0/100/300/750 ladder that held the
    // "Measuring overlay..." state for up to 750ms even when the overlay was ready.
    const delays = [0, 32, 80, 160];

    const tryMeasure = (delayIndex: number) => {
      if (generation !== this.overlayMeasureGeneration) {
        return;
      }
      if (this.measureOverlayMetrics()) {
        this.cancelOverlayMeasurement();
        return;
      }
      if (delayIndex >= delays.length - 1) {
        this.overlayMetrics = null;
        this.overlayMetricsError = 'Political overlay measurements are not available for this preview.';
        return;
      }
      const nextDelay = delays[delayIndex + 1];
      const timer = setTimeout(() => {
        this.window.requestAnimationFrame(() => {
          tryMeasure(delayIndex + 1);
        });
      }, nextDelay - delays[delayIndex]);
      this.overlayMeasureTimers.push(timer);
    };

    this.window.requestAnimationFrame(() => {
      tryMeasure(0);
    });
  }

  applyImageDimensions(width: number, height: number): void {
    if (_.isFinite(width) && width > 0) {
      this.form.creativeWidth = width;
    }
    if (_.isFinite(height) && height > 0) {
      this.form.creativeHeight = height;
    }
  }

  inspectImageUrl(url: string, onSuccess?: (image: HTMLImageElement) => void): void {
    if (!url) {
      return;
    }

    const image = new this.window.Image();
    image.onload = () => {
      // File/Image callbacks are outside Angular CD (zoneless) — markForCheck required.
      deferSignalStateWrite(() => {
        this.imageError = '';
        this.applyImageDimensions(image.naturalWidth, image.naturalHeight);
        if (_.isFunction(onSuccess)) {
          onSuccess(image);
        }
      });
    };
    image.onerror = () => {
      deferSignalStateWrite(() => {
        this.imageError = 'We could not load that image.';
      });
    };
    image.src = url;
  }

  refreshHostedImage(): void {
    this.form.imageSource = 'hosted';
    this.inspectImageUrl(this.form.hostedImageUrl);
  }

  onUploadSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    const file = files && files[0];
    this.imageError = '';

    if (!file) {
      return;
    }

    const reader = new this.window.FileReader();
    reader.onload = (loadEvent: ProgressEvent<FileReader>) => {
      const dataUrl = _.get(loadEvent, ['target', 'result'], '') as string;
      this.inspectImageUrl(dataUrl, () => {
        this.form.imageSource = 'upload';
        this.form.uploadedImageContent = dataUrl;
        this.form.uploadedImageMimeType = file.type || '';
        this.form.uploadedImageName = file.name || '';
        this.form.hostedImageUrl = this.form.hostedImageUrl || '';
      });
    };
    reader.onerror = () => {
      deferSignalStateWrite(() => {
        this.imageError = 'We could not read that upload.';
      });
    };
    reader.readAsDataURL(file);

    if (input) {
      input.value = '';
    }
  }

  clearUploadedImage(): void {
    this.form.uploadedImageContent = '';
    this.form.uploadedImageMimeType = '';
    this.form.uploadedImageName = '';
    if (this.form.hostedImageUrl) {
      this.form.imageSource = 'hosted';
    }
  }

  buildPreviewRequest(): any {
    const selectedElection = this.form.electionInputMode === 'existing' ? this.getSelectedElection() : null;
    const advertiserName = this.form.advertiserName || 'Political advertiser';
    const electionName = _.get(selectedElection, 'name') || this.form.electionName || 'Election';
    const electionDate = formatPoliticalPreviewDate(_.get(selectedElection, 'electionDate') || this.form.electionDate);
    const creativeWidth = Number(this.form.creativeWidth) || 1200;
    const creativeHeight = Number(this.form.creativeHeight) || 628;

    const layoutParameters = getPoliticalPreviewLayoutParameters(this.form);

    return {
      creativeWidth: creativeWidth,
      creativeHeight: creativeHeight,
      lineItemCode: 'preview-line-item',
      advertiserName: advertiserName,
      controllingEntityName: this.form.controllingEntitySameAsSponsor
        ? advertiserName
        : this.form.controllingEntityName,
      electionName: electionName,
      electionDate: electionDate,
      hasUserTargeting: !!this.form.hasUserTargeting,
      hasContextualTargeting: false,
      hasLocationTargeting: false,
      layoutParameters: layoutParameters,
      renderTemplate: getPoliticalPreviewRenderTemplate(this.form.layout)
    };
  }

  validateBeforeRender(): string {
    if (this.form.electionInputMode === 'existing') {
      if (!this.form.selectedElectionId) {
        return 'Select an election or switch to manual entry.';
      }
    } else {
      if (!this.form.electionName) {
        return 'Election name is required.';
      }
    }
    if (!this.form.advertiserName) {
      return 'Sponsor name is required.';
    }
    if (!this.form.controllingEntitySameAsSponsor && !this.form.controllingEntityName) {
      return 'Controlling entity name is required.';
    }
    if (!this.form.destinationUrl) {
      return 'Destination URL is required.';
    }
    if (!this.form.creativeWidth || Number(this.form.creativeWidth) < 1) {
      return 'Creative width must be greater than 0.';
    }
    if (!this.form.creativeHeight || Number(this.form.creativeHeight) < 1) {
      return 'Creative height must be greater than 0.';
    }
    if (!getPoliticalPreviewImageUrl(this.form)) {
      return 'Provide either a hosted image URL or an uploaded image.';
    }
    return '';
  }

  tryInitialRender(): void {
    if (this.hasAutoRendered || this.electionsLoading) {
      return;
    }
    if (this.form.electionInputMode === 'existing' && !this.form.selectedElectionId) {
      return;
    }
    this.hasAutoRendered = true;
    this.render();
  }

  render(): void {
    this.previewLanguage = normalizePreviewLanguageSelection(this.previewLanguage);
    this.error = this.validateBeforeRender();
    if (this.error) {
      return;
    }
    this.loading = true;
    this.publicError = null;
    this.hasPreview = true;
    this.resetOverlayMetrics();
    this.previewRequest = this.buildPreviewRequest();
    this.previewedLayout = this.form.layout;
    this.previewHtml = '';
    this.writePreviewIframe('');
    const imageUrl = getPoliticalPreviewImageUrl(this.form);
    const selectedLanguageCode = this.getSelectedLanguageCode();

    firstValueFrom(this.creativePreviewApi.render(this.previewRequest)).then((data: any) => {
      deferSignalStateWrite(() => {
        this.previewHtml = injectPoliticalPreviewHtml(data.document, this.form.destinationUrl, imageUrl, selectedLanguageCode);
        this.writePreviewIframe(this.previewHtml, imageUrl);
        this.loading = false;
      });
    }).catch((error: any) => {
      // Defer — Preview.$save can reject during an overlapping CD turn (NG0100 on
      // *ngIf="error" when '' flips to the failure message).
      deferSignalStateWrite(() => {
        this.hasPreview = false;
        this.previewedLayout = '';
        this.errorMessage = error;
        this.error = _.get(error, ['data', 'message'])
          || _.get(error, ['data', 'text'])
          || 'Unable to generate the political preview.';
        this.resetOverlayMetrics();
        this.writePreviewIframe('');
        this.loading = false;
      });
    });
  }

  onPreviewLanguageChange(): void {
    this.render();
  }

}
